import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Button,
  TextField,
  InputAdornment,
  Paper,
  Divider,
  Avatar,
  IconButton,
  Tooltip,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon,
  Public as PublicIcon,
  Language as LanguageIcon,
  Launch as LaunchIcon,
  FilterList as FilterIcon,
  PushPin as PinIcon,
  PushPinOutlined as PinOutlinedIcon,
  FlagSharp as GovIcon,
  Public as IntlIcon,
  Business as CorpIcon,
  Factory as IndIcon,
  School as UniIcon,
  VolunteerActivism as NgoIcon,
  RocketLaunch as VcIcon,
  Groups as ComIcon,
  CreditCard as CrowdIcon,
  Favorite as PerIcon,
  Apartment as FndIcon,
  Language as GlobalIcon,
  CompareArrows as BilateralIcon,
  Flag as NationalIcon,
  PublicOutlined as EurIcon,
  CheckCircle as OpenIcon,
  Schedule as UpcomingIcon,
  Cancel as ClosedIcon,
  Lightbulb as SmeIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import FollowButton from 'components/common/buttons/FollowButton';

const COOPERATION_TYPES = [
  { code: 'MUL', label: 'Multilateral', Icon: GlobalIcon },
  { code: 'BIL', label: 'Bilateral',                   Icon: BilateralIcon },
  { code: 'NAT', label: 'National',                    Icon: NationalIcon },
];

const AGENCY_CATEGORIES = [
  { code: 'NAT', label: 'National Funding',          Icon: GovIcon },
  { code: 'INT', label: 'International Organizations', Icon: IntlIcon },
  { code: 'SME', label: 'Innovation & SME',          Icon: SmeIcon },
  { code: 'PRV', label: 'Private Associations & Foundations',       Icon: FndIcon },
  { code: 'IND', label: 'Industry & Corporate',      Icon: IndIcon },
  { code: 'UNI', label: 'University Funding',          Icon: UniIcon },
  { code: 'VCT', label: 'Venture Capital',           Icon: VcIcon },
  { code: 'NGO', label: 'NGOs',                      Icon: NgoIcon },
  { code: 'PER', label: 'Philanthropic',             Icon: PerIcon },
  { code: 'COM', label: 'Community / Local',         Icon: ComIcon },
  { code: 'CFD', label: 'Crowdfunding',              Icon: CrowdIcon },
];

const REGIONS = [
  { code: 'europe',        label: 'Europe' },
  { code: 'north_america', label: 'North America' },
  { code: 'latin_america', label: 'Latin America & Caribbean' },
  { code: 'asia',          label: 'Asia' },
  { code: 'middle_east',   label: 'Middle East' },
  { code: 'africa',        label: 'Africa' },
  { code: 'oceania',       label: 'Oceania' },
  { code: 'global',        label: 'Global' },
];

const CALL_STATUSES = [
  { code: 'open',     label: 'Open',     Icon: OpenIcon,     color: 'success' },
  { code: 'upcoming', label: 'Upcoming', Icon: UpcomingIcon, color: 'info' },
  { code: 'closed',   label: 'Closed',   Icon: ClosedIcon,   color: 'default' },
];


// A compact filter dropdown: "All …" when nothing is chosen, the choice shown in the primary colour
function FilterSelect({ label, allLabel, value, onChange, options, loading }) {
  return (
    <TextField
      select size="small" label={label} value={value || ''} disabled={loading}
      onChange={(e) => onChange(e.target.value || null)}
      SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: 360 } } } }}
      InputLabelProps={{ shrink: true }}
      sx={{ minWidth: 200, flex: '0 1 240px',
        '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: 14, bgcolor: 'background.paper' },
        '& .MuiOutlinedInput-notchedOutline': value ? { borderColor: 'primary.main' } : {},
        '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 1, py: 0.9, fontSize: 14,
          color: value ? 'primary.main' : 'text.secondary', fontWeight: value ? 600 : 400 } }}>
      <MenuItem value=""><Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{allLabel}</Typography></MenuItem>
      {options.map(({ code, label: text, Icon }) => (
        <MenuItem key={code} value={code} sx={{ fontSize: 14, gap: 1, minHeight: 34 }}>
          {Icon && <Icon sx={{ fontSize: 18, color: 'text.secondary' }} />}{text}
        </MenuItem>
      ))}
    </TextField>
  );
}

const ScopeTile = styled(Paper)(({ theme, selected }) => ({
  padding: theme.spacing(3, 2),
  minHeight: 120,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  cursor: 'pointer',
  border: selected
    ? `2px solid ${theme.palette.primary.main}`
    : `1px solid ${theme.palette.divider}`,
  backgroundColor: selected
    ? `${theme.palette.primary.main}12`
    : theme.palette.background.paper,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: `${theme.palette.primary.main}08`,
    borderColor: theme.palette.primary.main,
    transform: 'translateY(-3px)',
    boxShadow: theme.shadows[3],
  },
}));

const StyledCard = styled(Card)(({ theme, selected }) => ({
  // minWidth: 275,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  border: selected ? `2px solid ${theme.palette.primary.main}` : '1px solid #e0e0e0',
  transform: selected ? 'scale(1.02)' : 'scale(1)',
  boxShadow: selected
    ? `0 8px 25px ${theme.palette.primary.main}20`
    : '0 2px 8px rgba(0,0,0,0.1)',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
  },
}));

const BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

// onShowMatchingCalls(words, filters): list every call matching the search, whatever its agency or program
const FundingAgencySelector = ({ onAgencySelect, selectedAgency, onPinAgency, refreshKey, pinnedIds = [],
  initialSearch = '', onShowMatchingCalls }) => {
  const theme = useTheme();
  const [agencies, setAgencies]                         = useState([]);
  const [loading, setLoading]                           = useState(false);
  const [searchTerm, setSearchTerm]                     = useState(initialSearch);
  const [selectedCoopType, setSelectedCoopType]             = useState(null);
  const [selectedCategory, setSelectedCategory]             = useState(null);
  const [selectedRegion, setSelectedRegion]                 = useState(null);
  const [availableRegionCodes, setAvailableRegionCodes]     = useState(null);
  const [selectedCallStatuses, setSelectedCallStatuses]     = useState(new Set());
  const [error, setError]                                   = useState(null);
  const [availableCoopCodes, setAvailableCoopCodes]         = useState(null);
  const [availableCategoryCodes, setAvailableCategoryCodes] = useState(null);

  const toggleCallStatus = (code) => {
    setSelectedCallStatuses(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  // Discover which cooperation types have data (the codes only: /funding/funding-agencies/facets/)
  useEffect(() => {
    setAvailableCoopCodes(null);
    fetch(`${BASE}/funding/funding-agencies/facets/`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => setAvailableCoopCodes(new Set(data.cooperation_types || [])))
      .catch(() => setAvailableCoopCodes(new Set()));
  }, [refreshKey]);

  // When cooperation type changes, reset category and reload available category chips
  useEffect(() => {
    setSelectedCategory(null);
    setAvailableCategoryCodes(null);
    const params = new URLSearchParams();
    if (selectedCoopType) params.set('cooperation_type', selectedCoopType);
    fetch(`${BASE}/funding/funding-agencies/facets/?${params}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        setAvailableCategoryCodes(new Set(data.categories || []));
        setAvailableRegionCodes(new Set(data.regions || []));
      })
      .catch(() => { setAvailableCategoryCodes(new Set()); setAvailableRegionCodes(new Set()); });
  }, [selectedCoopType, refreshKey]);

  // Fetch agencies whenever cooperation type, category, or call statuses change
  // Search words go to the server (?q=), which also looks inside each agency's programs and calls
  const [searchQuery, setSearchQuery] = useState(initialSearch.trim().length >= 2 ? initialSearch.trim() : '');
  const [searching, setSearching] = useState(false);
  const [matchingCalls, setMatchingCalls] = useState(null);     // how many calls match, across all agencies
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchTerm.trim().length >= 2 ? searchTerm.trim() : ''), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const fetchAgencies = useCallback(async (coopType, categoryCode, callStatuses, region, q = '') => {
    try {
      if (q) setSearching(true); else setLoading(true);   // keep the list on screen while searching
      setError(null);
      const params = new URLSearchParams({ page_size: '500' });
      if (q) params.set('q', q);
      if (coopType) params.set('cooperation_type', coopType);
      if (categoryCode) params.set('category', categoryCode);
      if (region) params.set('region', region);
      if (callStatuses && callStatuses.size > 0) params.set('call_status', [...callStatuses].join(','));
      const response = await fetch(`${BASE}/funding/funding-agencies/?${params}`, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch funding agencies');
      const data = await response.json();
      setAgencies(data.results || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    fetchAgencies(selectedCoopType, selectedCategory, selectedCallStatuses, selectedRegion, searchQuery);
  }, [selectedCoopType, selectedCategory, selectedCallStatuses, selectedRegion, refreshKey, fetchAgencies, searchQuery]);

  const filteredAgencies = agencies;      // already filtered by the server, search words included

  useEffect(() => {
    if (!searchQuery) { setMatchingCalls(null); return undefined; }
    let cancelled = false;
    const params = new URLSearchParams({ q: searchQuery, page_size: '1' });
    if (selectedCoopType) params.set('cooperation_type', selectedCoopType);
    if (selectedCallStatuses.size > 0) params.set('statuses', [...selectedCallStatuses].join(','));
    fetch(`${BASE}/funding/calls/?${params}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setMatchingCalls(d ? d.count : null); })
      .catch(() => { if (!cancelled) setMatchingCalls(null); });
    return () => { cancelled = true; };
  }, [searchQuery, selectedCoopType, selectedCallStatuses]);


  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3, pt:3 }}>
      {/* Header */}
      <Box mb={3} textAlign="center"> 
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Select Funding Sources
        </Typography>
       <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Browse funding agencies, programs and calls
        </Typography>
      </Box>

      {/* Cooperation Scope — tile cards */}
      <Paper elevation={1} sx={{ p: 3, mb:3 }}>
        <Box display="flex" alignItems="center" gap={1} >
          {/* <FilterIcon color="action" /> */}
          {/* <Typography variant="subtitle1" fontWeight="600">
            Cooperation Scope
          </Typography> */}
          {selectedCoopType && (
            <Chip
              label="Clear"
              size="small"
              onDelete={() => setSelectedCoopType(null)}
              sx={{ ml: 'auto' }}
            />
          )}
        </Box>
        {/* the scope cards share the row, each between 160 and 280px wide */}
        <Box display="flex" gap={2} flexWrap="wrap" mb={3}>
          {availableCoopCodes === null ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Box sx={{ flex: '1 1 0', minWidth: 160, maxWidth: 180 }} key={i}>
                <ScopeTile elevation={1} sx={{ opacity: 0.4 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'action.hover', mb: 1 }} />
                  <Box sx={{ width: '70%', height: 14, borderRadius: 1, bgcolor: 'action.hover' }} />
                </ScopeTile>
              </Box>
            ))
          ) : (
            COOPERATION_TYPES.filter(({ code }) => availableCoopCodes.has(code)).map(({ code, label, Icon }) => (
              <Box sx={{ flex: '1 1 0', minWidth: 160, maxWidth: 220 }} key={code}>
                <ScopeTile
                  elevation={selectedCoopType === code ? 4 : 1}
                  selected={selectedCoopType === code}
                  onClick={() => setSelectedCoopType(prev => prev === code ? null : code)}
                >
                  <Icon
                    sx={{ fontSize: 40, mb: 1 }}
                    color={selectedCoopType === code ? 'primary' : 'action'}
                  />
                  <Typography
                    variant="body2"
                    fontWeight={selectedCoopType === code ? 700 : 500}
                    color={selectedCoopType === code ? 'primary' : 'text.secondary'}
                    sx={{ lineHeight: 1.4 }}
                  >
                    {label}
                  </Typography>
                </ScopeTile>
              </Box>
            ))
          )}
        </Box>
      {/* </Paper> */}

      {/* Region and agency type dropdowns side by side, the call status chips after them: beside them when there
          is room (Quick Access collapsed), on the next line when there is not */}
        <Box display="flex" alignItems="center" columnGap={3} rowGap={2} mb={2} flexWrap="wrap">
          <FilterSelect label="Region" allLabel="All regions" value={selectedRegion} onChange={setSelectedRegion}
            loading={availableRegionCodes === null}
            options={REGIONS.filter(({ code }) => availableRegionCodes?.has(code))
              .map((r) => ({ ...r, Icon: PublicIcon }))} />
          <FilterSelect label="Agency type" allLabel="All agency types" value={selectedCategory} onChange={setSelectedCategory}
            loading={availableCategoryCodes === null}
            options={AGENCY_CATEGORIES.filter(({ code }) => availableCategoryCodes?.has(code))} />

          <Box display="flex" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
            {/* <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mr: 0.5 }}>
              Call status:
            </Typography> */}
            {CALL_STATUSES.map(({ code, label, Icon, color }) => (
              <Chip
                key={code}
                icon={<Icon fontSize="small" />}
                label={label}
                size="small"
                onClick={() => toggleCallStatus(code)}
                color={selectedCallStatuses.has(code) ? color : 'default'}
                variant={selectedCallStatuses.has(code) ? 'filled' : 'outlined'}
                sx={{ fontWeight: selectedCallStatuses.has(code) ? 700 : 400 }}
              />
            ))}
            {selectedCallStatuses.size > 0 && (
              <Chip label="Clear" size="small" onDelete={() => setSelectedCallStatuses(new Set())} />
            )}
          </Box>
        </Box>

      {/* Search bar + active filter summary + count */}
      {/* <Paper elevation={1} sx={{ p: 2, mb: 3 }}> */}
        {/* the choices show on the cards, dropdowns and status chips above: no summary chips repeated here */}
        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search agencies, their programs and calls…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flexGrow: 1, minWidth: 200, maxWidth: 700 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searching ? (
                <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment>
              ) : null,
            }}
          />
          {!loading && (
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {filteredAgencies.length} agenc{filteredAgencies.length !== 1 ? 'ies' : 'y'}
            </Typography>
          )}
          {searchQuery && onShowMatchingCalls && matchingCalls > 0 && (
            <Button variant="contained" size="small" sx={{ whiteSpace: 'nowrap' }}
              onClick={() => onShowMatchingCalls(searchQuery, { cooperationType: selectedCoopType, callStatuses: [...selectedCallStatuses] })}>
              Show all {matchingCalls.toLocaleString()} matching call{matchingCalls === 1 ? '' : 's'}
            </Button>
          )}
        </Box>
      </Paper>

      {/* Agency list */}
      {loading && (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="300px">
          <CircularProgress size={60} thickness={4} />
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            Loading funding agencies...
          </Typography>
        </Box>
      )}

      {!loading && error && (
        <Box display="flex" justifyContent="center" minHeight="200px" alignItems="center">
          <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="error" gutterBottom>Error Loading Agencies</Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>{error}</Typography>
            <Button variant="contained" onClick={() => fetchAgencies(selectedCoopType, selectedCategory)} sx={{ mt: 2 }}>
              Retry
            </Button>
          </Paper>
        </Box>
      )}

      {!loading && !error && filteredAgencies.length === 0 && (
        <Box textAlign="center" py={8}>
          <Typography variant="h6" color="text.secondary">
            {searchTerm
              ? `No agencies matching "${searchTerm}"`
              : 'No agencies found for the selected filters'}
          </Typography>
        </Box>
      )}

      {!loading && !error && filteredAgencies.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 3,
          }}
        >
          {filteredAgencies.map((agency) => (
              <StyledCard
                key={agency.id}
                selected={selectedAgency?.id === agency.id}
                onClick={() => onAgencySelect(agency, { cooperationType: selectedCoopType, callStatuses: [...selectedCallStatuses], search: searchQuery })}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Avatar sx={{
                      width: 56, height: 56,
                      fontSize: agency.acronym && agency.acronym.length <= 5 ? (agency.acronym.length > 3 ? '1.3rem' : '1.6rem') : '2.2rem',
                      fontWeight: 700,
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                    }}>
                      {agency.acronym && agency.acronym.length <= 5 ? agency.acronym : agency.name.charAt(0).toUpperCase()}
                    </Avatar>


                    
             
                  <Box display="flex" justifyContent="flex-end" alignItems="center">
                    <FollowButton
                      contentType="fundingagency"
                      objectId={agency.id}
                      size="medium"
                      variant="outlined"
                    />


                {onPinAgency && (
                      <Tooltip title={pinnedIds.includes(agency.id) ? 'Pinned' : 'Pin to Quick Access'}>
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={(e) => { e.stopPropagation(); onPinAgency(agency); }}
                        >
                          {pinnedIds.includes(agency.id) ? <PinIcon /> : <PinOutlinedIcon />}
                        </IconButton>
                      </Tooltip>
                    )}
           </Box> 

                  </Box>

                  <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {agency.name}
                    {agency.abbreviation && (
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        ({agency.abbreviation})
                      </Typography>
                    )}
                
                    {agency.website && (
                      <Tooltip title="Visit Website">
                        <IconButton
                          color="default"
                          size="small"
                          onClick={(e) => { e.stopPropagation(); window.open(agency.website, '_blank', 'noopener,noreferrer'); }}
                        >
                          <LaunchIcon />
                        </IconButton>
                      </Tooltip>
                    )}

  

              
                    
                  </Typography>




                  <Box display="flex" alignItems="center" mb={2}>
                    <PublicIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      {agency.country}
                    </Typography>
                  </Box>

                  {agency.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
                      {agency.description}
                    </Typography>
                  )}

                  <Box display="flex" gap={2} >
                    {/* while searching: only the programs and calls that match (the totals in the tooltip) */}
                    <Tooltip title={searchQuery ? `${agency.programs_count || 0} programs in total` : 'Programs'}>
                      <Chip
                        label={searchQuery ? `${agency.matched_programs || 0} matching programs` : `${agency.programs_count || 0} Programs`}
                        size="small"
                        variant="outlined"
                        icon={<LanguageIcon />}
                      />
                    </Tooltip>
                    <Tooltip title={searchQuery ? `${agency.active_calls_count || 0} calls in total` : 'Calls'}>
                      <Chip
                        label={searchQuery
                          ? `${agency.matched_calls || 0} matching calls`
                          : `${agency.active_calls_count || 0} ${selectedCallStatuses.size > 0 ? [...selectedCallStatuses].map(s => s.charAt(0).toUpperCase() + s.slice(1)).join('/') + ' ' : ''}Calls`}
                        size="small"
                        variant="outlined"
                        color={(searchQuery ? agency.matched_calls : agency.active_calls_count) > 0 ? 'success' : 'default'}
                      />
                    </Tooltip>
                  </Box>

                  {/* <Divider sx={{ my: 2 }} /> */}

                  {/* 
                    <Button
                      variant={selectedAgency?.id === agency.id ? 'contained' : 'outlined'}
                      color="primary"
                      onClick={(e) => { e.stopPropagation(); onAgencySelect(agency, { cooperationType: selectedCoopType, callStatuses: [...selectedCallStatuses] }); }}
                      sx={{ flexGrow: 1, mr: 1 }}
                    >
                      {selectedAgency?.id === agency.id ? 'Selected' : 'Select Agency'}
                    </Button>*/}

       
                </CardContent>
              </StyledCard>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default FundingAgencySelector;
